import { useState, useRef, useEffect, useCallback } from 'react';
import { closeOnOtherPanel } from '../lib/appPanels';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Send, Loader2, Sparkles, RefreshCw, ArrowRight, Phone, MapPin, Recycle, Bus, Train, Bike, Car, Calendar, ShoppingBag, AlertTriangle, Users, BarChart3, Eye, FileText, Cloud, LayoutGrid, Zap, Shield, Fuel, HeartHandshake, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { startDictation, stopDictation, canDictate, speak, stopSpeaking, canSpeak, speechPreview, onSpeechState } from '../lib/utils';
import { assistantAPI } from '../lib/api';
import { DEFAULT_MSG_LENGTH, messageLimit } from '../lib/torreLimits';

const SESSION_KEY = 'torre_session';

// Every quick question maps to data Torre actually has (checked nightly by the Torre evals).
const ALL_QUICK_QUESTIONS = [
  { text: '¿Farmacia de guardia hoy?', icon: MapPin },
  { text: '¿Qué eventos hay esta semana?', icon: Calendar },
  { text: '¿Cómo me empadrono?', icon: FileText },
  { text: '¿Próximo tren a Santander?', icon: Train },
  { text: '¿Qué tiempo hace hoy?', icon: Cloud },
  { text: '¿Dónde tiro el aceite usado?', icon: Recycle },
  { text: '¿Hay plazas libres en la ERA?', icon: Car },
  { text: '¿Cuándo juega la Gimnástica?', icon: Calendar },
  { text: '¿Cuál es la gasolinera más barata?', icon: Fuel },
  { text: '¿Está activa la ZBE ahora?', icon: Shield },
  { text: '¿Cómo reporto un bache?', icon: AlertTriangle },
  { text: '¿Precio de la luz ahora?', icon: Zap },
];

const ACTION_ICONS = {
  recycle: Recycle, bus: Bus, train: Train, bike: Bike, car: Car,
  calendar: Calendar, shop: ShoppingBag, alert: AlertTriangle,
  users: Users, chart: BarChart3, eye: Eye, file: FileText,
  cloud: Cloud, grid: LayoutGrid, map: MapPin, phone: Phone, zap: Zap, shield: Shield, fuel: Fuel,
};

function getRandomQuestions(count = 4) {
  const shuffled = [...ALL_QUICK_QUESTIONS].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

function readSession() {
  try { return localStorage.getItem(SESSION_KEY) || ''; } catch { return ''; }
}

function saveSession(token) {
  try { if (token) localStorage.setItem(SESSION_KEY, token); } catch { /* storage unavailable */ }
}

// Spanish phone numbers (9 digits starting 6-9, optional spaces) and the 3-digit emergency lines
// become tel: links.
const PHONE_RE = /\b[6-9]\d{2}[ ]?\d{2}[ ]?\d{2}[ ]?\d{2}\b|\b[6-9]\d{2}[ ]?\d{3}[ ]?\d{3}\b|\b(?:112|016|024)\b/g;
// A bare 112/016/024 is a phone only when the words just before it say so ("llama al 112",
// "Violencia de género: 016", "el 024 te atiende"); "Hay 112 plazas libres" is a count.
// No lookbehind in the regexes: older iOS WebViews reject it and the whole bundle would fail.
const PHONE_CONTEXT_RE = /(?:llam|marc|tel[eé]fono|tlf|emergencia|urgencia|contacto|violencia|g[eé]nero|\b[ae]l\s*$)[^.\n]{0,20}$/i;

function splitPhones(text) {
  const parts = [];
  let last = 0;
  let m;
  PHONE_RE.lastIndex = 0;
  while ((m = PHONE_RE.exec(text))) {
    if (m[0].length === 3 && !PHONE_CONTEXT_RE.test(text.slice(Math.max(0, m.index - 30), m.index))) continue;
    parts.push({ text: text.slice(last, m.index) }, { text: m[0], phone: true });
    last = m.index + m[0].length;
  }
  parts.push({ text: text.slice(last) });
  return parts;
}

function ReplyText({ text }) {
  const parts = splitPhones(String(text || ''));
  return (
    <span className="whitespace-pre-line">
      {parts.map((part, i) => (part.phone
        ? <a key={i} href={`tel:${part.text.replace(/\s/g, '')}`} className="font-semibold underline decoration-dotted underline-offset-2">{part.text}</a>
        : <span key={i}>{part.text}</span>))}
    </span>
  );
}

function ActionButton({ action, onNavigate }) {
  const Icon = ACTION_ICONS[action.icon] || ArrowRight;

  if (action.type === 'phone') {
    return (
      <a
        href={`tel:${action.number}`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-colors"
        data-testid={`action-phone-${action.number}`}
      >
        <Phone size={12} aria-hidden="true" />
        {action.label}
      </a>
    );
  }

  if (action.type === 'link') {
    return (
      <button
        onClick={() => onNavigate(action.path)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-colors"
        data-testid={`action-link-${action.path}`}
      >
        <Icon size={12} aria-hidden="true" />
        {action.label}
        <ArrowRight size={10} aria-hidden="true" />
      </button>
    );
  }

  return null;
}

function bubbleClass(msg) {
  if (msg.role === 'user') return 'bg-indigo-600 text-white rounded-br-md';
  if (msg.scope === 'urgencia') return 'bg-rose-50 text-rose-900 border border-rose-200 rounded-bl-md';
  if (msg.error) return 'bg-amber-50 text-amber-800 border border-amber-200 rounded-bl-md';
  return 'bg-slate-100 text-slate-800 rounded-bl-md';
}

export default function CityAssistant({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [dictating, setDictating] = useState(false);
  // Why the microphone could not start: 'not-allowed' (permission refused) or 'no-mic'. A refusal
  // used to just stop the listening view, so after one "No permitir" the microphone did nothing
  // on every later tap, with no word about why or where to change it.
  const [micError, setMicError] = useState(null);
  const micErrorRef = useRef(null);
  // It lands under the suggestions, below the fold of the sheet: bring it into view, as the
  // listening view is.
  useEffect(() => {
    if (micError && micErrorRef.current?.scrollIntoView) micErrorRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [micError]);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [voiceAvailable] = useState(() => canSpeak());
  // Only an answer to a spoken question is read back unprompted. Someone who typed their question
  // is looking at the screen, and a phone that starts talking on its own is a bug to them.
  const askedByVoice = useRef(false);
  const [micAvailable] = useState(() => canDictate());
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(readSession);
  const [remaining, setRemaining] = useState(null);
  const [quickQuestions] = useState(() => getRandomQuestions(4));
  // The admin's limit, read once the sheet is first opened (lib/torreLimits.js). A ref as well,
  // for the dictation callback, which is created once.
  const [maxLength, setMaxLength] = useState(DEFAULT_MSG_LENGTH);
  const maxLengthRef = useRef(DEFAULT_MSG_LENGTH);
  const configAsked = useRef(false);
  const loadLimit = useCallback(() => {
    configAsked.current = true;
    assistantAPI.config()
      .then((res) => { const n = messageLimit(res.data); maxLengthRef.current = n; setMaxLength(n); })
      .catch(() => { configAsked.current = false; });   // try again next time it opens; 200 meanwhile
  }, []);
  useEffect(() => {
    if (open && !configAsked.current) loadLimit();
  }, [open, loadLimit]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sendMessageRef = useRef(null);
  // A fresh page (or "Nueva conversación") starts a new server-side conversation so Torre never
  // remembers turns the user can't see. The device (and its daily quota) is kept.
  const freshConversation = useRef(true);
  // Bumped by "Nueva conversación": a reply to a question from an earlier conversation is dropped
  // rather than landing in the new, empty thread.
  const conversationId = useRef(0);

  // The daily limit belongs to whoever is asking. Signing in (which the limit reply suggests)
  // or out starts from the server's next count, not from the guest's 0.
  const userId = user?.id || null;
  useEffect(() => { setRemaining(null); }, [userId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Focus: into the panel when it opens (the input on desktop, where there is no virtual keyboard
  // to pop up; the title otherwise), and back to what opened it when it closes, or to the bubble.
  // Not after a navigation: the reader is on another page by then.
  const headingRef = useRef(null);
  const bubbleRef = useRef(null);
  const returnFocus = useRef(null);
  const openedOnPath = useRef(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      const active = document.activeElement;
      returnFocus.current = active && active !== document.body ? active : null;
      openedOnPath.current = window.location.pathname;
      if (inputRef.current && !inputRef.current.disabled && window.innerWidth >= 768) inputRef.current.focus();
      else headingRef.current?.focus();
    } else if (wasOpen.current) {
      wasOpen.current = false;
      if (window.location.pathname !== openedOnPath.current) return undefined;
      const back = returnFocus.current;
      if (back && back.isConnected) back.focus();
      else bubbleRef.current?.focus();
    }
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // A navigation the assistant did not make itself — the native tab bar, a row in Servicios, a
  // link in the header — should leave the reader on the page they asked for. handleNavigate
  // closes the sheet for links inside an answer, but nothing closed it for the rest, so in the
  // app the sheet stayed draped over whatever tab was tapped next.
  const lastPath = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname === lastPath.current) return;
    lastPath.current = location.pathname;
    // Except the one navigation whose whole purpose is to open it.
    if (new URLSearchParams(location.search).get('open_assistant') === 'true') return;
    setOpen(false);
  }, [location.pathname, location.search]);

  // Auto-open from URL param (native app's Más → AI chat).
  // Reacts to SPA navigations too — native nativeDeepLink navigation does NOT remount
  // this component, so a mount-only effect misses the param. Runs on mount as well.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('open_assistant') === 'true') {
      setOpen(true);
      // Clean via the router (keeps router state in sync), but only AFTER the native app's
      // 300 ms deep-link check: mobile SharedWebView force-reloads the page if the URL no longer
      // matches the path it sent. Cleaning is still needed so tapping "Asistente IA" again
      // re-triggers. If the user navigates away first, the timer is cancelled.
      const timer = setTimeout(() => navigate(location.pathname, { replace: true }), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetConversation = () => {
    conversationId.current += 1;
    freshConversation.current = true;
    askedByVoice.current = false;
    setMessages([]);
    setLoading(false);
  };

  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };

  // The sheet's body scrolls, and the listening panel sits after the conversation — so on a
  // short screen, or with a few messages already in the thread, the thing telling you the mic is
  // live can open below the fold. Pull it into view as soon as it appears.
  const listeningRef = useRef(null);
  useEffect(() => {
    if (!dictating) return;
    const el = listeningRef.current;
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [dictating]);

  // Stopping the mic yourself is a cancel, not a question — so it must not send what was heard
  // so far. Only a recogniser that ended on its own, having decided you finished speaking, does.
  const autoSend = useRef(true);

  const beginDictation = useCallback(() => {
    autoSend.current = true;
    const started = startDictation({
      // Interim results stream into the field so the words appear as they are recognised; the
      // final one is the question. The recogniser endpoints on silence (continuous: false), so
      // finishing a sentence is what sends it — there is no button to find at the end.
      onUpdate: ({ text, isFinal }) => {
        askedByVoice.current = true;
        const said = text.slice(0, maxLengthRef.current);
        setInput(said);
        if (!isFinal || !autoSend.current) return;
        // A recogniser that heard nothing useful returns an empty or one-character result.
        // Sending that spends one of the day's questions on a cough.
        if (said.trim().length < 2) { setDictating(false); return; }
        setDictating(false);
        setInput('');
        sendMessageRef.current?.(said);
      },
      onEnd: () => setDictating(false),
      onError: (code) => {
        setDictating(false);
        if (code === 'not-allowed' || code === 'service-not-allowed') setMicError('not-allowed');
        else if (code === 'no-mic' || code === 'audio-capture') setMicError('no-mic');
      },
    });
    if (started) { setDictating(true); setMicError(null); }
    return started;
  }, []);

  // The app's top bar has a microphone that should do the whole thing in one tap: open the
  // sheet and start listening. Opening and listening are separate frames on purpose — the
  // recogniser wants the sheet mounted so the transcript has somewhere visible to land, and a
  // reader needs to see it is listening before it starts.
  useEffect(() => {
    const onVoice = () => {
      setOpen(true);
      if (!canDictate()) return;
      requestAnimationFrame(() => setTimeout(beginDictation, 250));
    };
    window.addEventListener('somostorre:open-assistant-voice', onVoice);
    return () => window.removeEventListener('somostorre:open-assistant-voice', onVoice);
  }, [beginDictation]);
  useEffect(() => closeOnOtherPanel('assistant', () => setOpen(false)), []);

  const readAloud = useCallback((idx, text) => {
    if (speakingIdx === idx) { stopSpeaking(); setSpeakingIdx(null); return; }
    const { preview } = speechPreview(text);
    stopSpeaking();
    if (speak(preview)) setSpeakingIdx(idx);
  }, [speakingIdx]);

  // Reflect what the voice is actually doing, not what we last asked for: a reading that ends on
  // its own must clear the button, or it sits on "Detener" forever.
  useEffect(() => onSpeechState((state) => {
    if (state !== 'start') setSpeakingIdx(null);
  }), []);

  // Nothing should still be talking once the sheet is gone or the reader has moved on.
  useEffect(() => () => stopSpeaking(), []);
  useEffect(() => { if (!open) { stopSpeaking(); setSpeakingIdx(null); } }, [open]);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    // The question was asked another way: the microphone notice no longer applies (it stayed under the answer).
    setMicError(null);
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    const conv = conversationId.current;
    const stale = () => conv !== conversationId.current;

    try {
      const res = await assistantAPI.chat(msg, sessionToken, freshConversation.current);
      const data = res.data || {};
      // The device token and the day's count hold whichever conversation this answer was for
      if (data.session_token) {
        setSessionToken(data.session_token);
        saveSession(data.session_token);
      }
      if (typeof data.remaining === 'number') setRemaining(data.remaining);
      // Lowered in admin since the sheet opened: the server said so; the box follows.
      if (data.error === 'too_long') loadLimit();
      if (stale()) return;
      freshConversation.current = false;
      const replyText = data.reply || 'No pude procesar tu pregunta.';
      setMessages(prev => {
        const next = [...prev, {
          role: 'assistant',
          text: replyText,
          error: data.error,
          scope: data.scope,
          actions: data.actions || [],
          sources: [...new Set((data.sources || []).map(s => s.label))],
        }];
        // Asked out loud, answered out loud — the point of the whole loop. Capped to the first
        // couple of sentences, because a listener cannot skim a list of nineteen pharmacies.
        if (askedByVoice.current && canSpeak()) {
          const { preview } = speechPreview(replyText);
          if (speak(preview)) setSpeakingIdx(next.length - 1);
        }
        askedByVoice.current = false;
        return next;
      });
    } catch {
      if (stale()) return;
      setMessages(prev => [...prev, { role: 'assistant', text: 'Error de conexión. Inténtalo de nuevo.', error: 'network', actions: [] }]);
    } finally {
      if (!stale()) setLoading(false);
    }
  }, [input, loading, sessionToken, loadLimit]);

  // Keep ref in sync with latest sendMessage
  sendMessageRef.current = sendMessage;

  // Expose global function for Torre IA bar to pre-fill and send questions
  useEffect(() => {
    window.__torreAskQuestion = (question) => {
      setOpen(true);
      setTimeout(() => sendMessageRef.current?.(question), 300);
    };
    // Open the panel without sending anything.
    //
    // Every surface that wanted this was doing it by finding the floating bubble in the DOM and
    // clicking it — but the bubble is conditional (`!open && !hideBubble && !isMapPage`), so
    // while the cookie banner is up, during onboarding, or on /mapa there is nothing to find and
    // the click silently does nothing. The Residuos and Trámites cards inherited that from the
    // Inicio banner. A bridge cannot be missing the way an element can.
    window.__torreOpen = () => setOpen(true);
    return () => { delete window.__torreAskQuestion; delete window.__torreOpen; };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const charsLeft = maxLength - input.length;
  const outOfQuestions = remaining === 0;

  // Hide the chat bubble only while the cookie banner or the onboarding tour is on screen, and on the
  // map page. Onboarding has been opt-in since May 2026, so the bubble must NOT wait for it to be
  // completed — doing so hid Torre from every first-time visitor.
  const [cookieBannerUp, setCookieBannerUp] = useState(() => {
    try { return !localStorage.getItem('torre_cookie_consent'); } catch { return false; }
  });
  const [onboardingUp, setOnboardingUp] = useState(false);
  const hideBubble = cookieBannerUp || onboardingUp;
  const pathname = window.location.pathname;
  const isMapPage = pathname === '/mapa' || pathname === '/mapa/';

  useEffect(() => {
    const onCookie = (e) => setCookieBannerUp(!!e.detail?.visible);
    const onOnboarding = (e) => setOnboardingUp(!!e.detail?.visible);
    window.addEventListener('cookie-banner-visibility', onCookie);
    window.addEventListener('onboarding-visibility', onOnboarding);
    return () => {
      window.removeEventListener('cookie-banner-visibility', onCookie);
      window.removeEventListener('onboarding-visibility', onOnboarding);
    };
  }, []);

  return (
    <>
      {/* Chat bubble — hidden in native via CSS [data-testid="assistant-bubble"] */}
      {!open && !hideBubble && !isMapPage && (
        <button
          ref={bubbleRef}
          onClick={() => setOpen(true)}
          aria-label="Abrir Pregunta a Torre, el asistente de Torrelavega"
          className="fixed bottom-24 right-4 lg:bottom-8 lg:right-6 z-[1050] w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
          data-testid="assistant-bubble"
        >
          <Sparkles size={22} className="group-hover:rotate-12 transition-transform" aria-hidden="true" />
          <span className="absolute inset-0 rounded-full bg-indigo-500/30 animate-ping" style={{ animationDuration: '3s' }} aria-hidden="true" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          role="dialog"
          // Open as far as the app is concerned (lib/nativeModalBridge.js counts [role=dialog][data-state=open]): its back
          // closes the panel instead of leaving the app (Android emulator, 10 Oct 2026).
          data-state="open"
          aria-labelledby="torre-panel-title"
          className="fixed bottom-20 right-3 left-3 sm:left-auto sm:bottom-24 sm:right-4 lg:bottom-8 lg:right-6 z-[1050] sm:w-[340px] md:w-[380px] animate-fade-in-up torre-panel"
          data-testid="assistant-panel"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col" style={{ maxHeight: 'min(70vh, 520px)' }}>
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 flex items-center gap-3" data-testid="assistant-header">
              <div className="w-9 h-9 bg-white/15 rounded-full flex items-center justify-center backdrop-blur-sm" aria-hidden="true">
                <Sparkles size={16} className="text-amber-300" />
              </div>
              <div className="flex-1">
                <p id="torre-panel-title" ref={headingRef} tabIndex={-1} className="text-sm font-extrabold text-white focus:outline-none">Pregunta a Torre</p>
                <p className="text-[11px] text-white/70">
                  {remaining !== null
                    ? `${remaining} ${remaining === 1 ? 'pregunta restante' : 'preguntas restantes'} hoy`
                    : 'Datos oficiales de Torrelavega'}
                </p>
              </div>
              {messages.length > 0 && (
                <button onClick={resetConversation} aria-label="Nueva conversación" title="Nueva conversación"
                  className="p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all" data-testid="new-conversation">
                  <RefreshCw size={15} />
                </button>
              )}
              <button onClick={() => setOpen(false)} aria-label="Cerrar" className="p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all" data-testid="assistant-close">
                <X size={16} />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              role="log"
              aria-live="polite"
              aria-relevant="additions"
              className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
              style={{ minHeight: '200px', maxHeight: '360px' }}
              data-testid="assistant-messages"
            >
              {!dictating && messages.length === 0 && (
                <div className="text-center py-3">
                  <div className="w-12 h-12 bg-indigo-50 rounded-full mx-auto mb-3 flex items-center justify-center" aria-hidden="true">
                    <Sparkles size={20} className="text-indigo-400" />
                  </div>
                  <p className="text-sm font-bold text-slate-800">¡Hola, vecino!</p>
                  <p className="text-xs text-slate-500 mt-1 mb-3">Respondo sobre Torrelavega con los datos de SomosTorre</p>
                  {/* Talking is the easy way in for many readers, so it gets the biggest target on
                      the empty sheet, not a 32px icon inside the text field (2026-09-24). */}
                  {micAvailable && !outOfQuestions && (
                    <button type="button" onClick={() => beginDictation()} disabled={loading}
                      className="mx-auto mb-4 flex flex-col items-center gap-1.5 group" data-testid="assistant-mic-big">
                      <span className="w-16 h-16 rounded-full bg-indigo-600 group-hover:bg-indigo-700 text-white flex items-center justify-center shadow-lg shadow-indigo-600/25 transition-colors">
                        <Mic size={28} aria-hidden="true" />
                      </span>
                      <span className="text-sm font-bold text-indigo-700">Pregunta hablando</span>
                    </button>
                  )}
                  <div className="space-y-1.5">
                    {quickQuestions.map(q => (
                      <button
                        key={q.text}
                        onClick={() => sendMessage(q.text)}
                        className="w-full text-left text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-2 rounded-lg transition-colors font-medium flex items-center gap-2"
                        data-testid={`quick-q-${q.text.slice(0, 15).replace(/\s/g, '-')}`}
                      >
                        <q.icon size={13} className="text-indigo-400 shrink-0" aria-hidden="true" />
                        {q.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`} data-testid={`msg-${i}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${bubbleClass(msg)}`}>
                    {msg.scope === 'urgencia' && <HeartHandshake size={14} className="inline mr-1 -mt-0.5 text-rose-500" aria-hidden="true" />}
                    {msg.role === 'assistant' ? <ReplyText text={msg.text} /> : msg.text}
                    {msg.sources && msg.sources.length > 0 && (
                      <span className="block text-[11px] text-slate-500 mt-1.5" data-testid={`sources-${i}`}>
                        Fuente: {msg.sources.join(' · ')}
                      </span>
                    )}
                  </div>
                  {msg.role === 'assistant' && !msg.error && voiceAvailable && (
                    <button
                      type="button"
                      onClick={() => readAloud(i, msg.text)}
                      aria-pressed={speakingIdx === i}
                      aria-label={speakingIdx === i ? 'Detener la lectura' : 'Escuchar la respuesta'}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-torre-teal hover:underline"
                      data-testid={`assistant-speak-${i}`}
                    >
                      {speakingIdx === i
                        ? <><VolumeX size={12} /> Detener</>
                        : <><Volume2 size={12} /> Escuchar</>}
                    </button>
                  )}
                  {/* Action buttons */}
                  {msg.role === 'assistant' && msg.actions && msg.actions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2 max-w-[90%]" data-testid={`actions-${i}`}>
                      {msg.actions.map((action, j) => (
                        <ActionButton key={j} action={action} onNavigate={handleNavigate} />
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Listening takes over the sheet rather than sitting quietly beside a chat box.
                  Someone who tapped the microphone came to talk: they need to see that it is
                  live, watch the words land, and be able to stop — without hunting for a small
                  control. It replaces the body only while listening, so the conversation is
                  untouched underneath and reappears the moment it ends. */}
              {!dictating && micError && (
                <div ref={micErrorRef} className="mx-1 my-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-left" role="status" data-testid="assistant-mic-error">
                  <p className="text-xs text-amber-900 leading-relaxed">
                    {micError === 'not-allowed'
                      ? 'No tengo permiso para usar el micrófono. Puedes escribir tu pregunta'
                        + (typeof window !== 'undefined' && window.__openSettings ? ' o permitirlo en Ajustes.' : ' o permitirlo en los ajustes del navegador para este sitio.')
                      : 'No encuentro un micrófono disponible. Puedes escribir tu pregunta.'}
                  </p>
                  {micError === 'not-allowed' && typeof window !== 'undefined' && window.__openSettings && (
                    <button
                      type="button"
                      onClick={() => window.__openSettings?.()}
                      className="mt-2 text-xs font-bold text-torre-teal hover:underline"
                      data-testid="assistant-mic-settings"
                    >
                      Abrir Ajustes
                    </button>
                  )}
                </div>
              )}

              {dictating && (
                <div ref={listeningRef} className="py-4 text-center" data-testid="assistant-listening" role="status" aria-live="polite">
                  <button
                    type="button"
                    onClick={() => { autoSend.current = false; stopDictation(); setDictating(false); }}
                    aria-label="Detener la escucha"
                    className="relative w-16 h-16 mx-auto mb-3 rounded-full bg-indigo-600 text-white flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-indigo-300"
                    data-testid="assistant-listening-stop"
                  >
                    <span className="absolute inset-0 rounded-full bg-indigo-400/40 animate-ping" aria-hidden="true" />
                    <Mic size={26} className="relative" />
                  </button>
                  <p className="text-sm font-bold text-torre-slate">Te escucho…</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {input ? 'Toca el micrófono cuando termines' : 'Habla con normalidad'}
                  </p>
                  {input && (
                    <p className="mt-2 mx-auto max-w-xs text-base text-torre-slate leading-relaxed line-clamp-3" data-testid="assistant-live-transcript">
                      {input}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => { autoSend.current = false; stopDictation(); setDictating(false); }}
                    className="mt-3 text-xs font-bold text-torre-teal hover:underline"
                    data-testid="assistant-listening-type"
                  >
                    Prefiero escribir
                  </button>
                </div>
              )}

              {loading && (
                <div className="flex justify-start" data-testid="assistant-loading">
                  <div className="bg-slate-100 px-4 py-2.5 rounded-2xl rounded-bl-md flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin text-indigo-500" aria-hidden="true" />
                    <span className="text-xs text-slate-500">Torre está consultando los datos…</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="px-3 py-2.5 border-t border-slate-100" data-testid="assistant-input-form">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <label htmlFor="torre-input" className="sr-only">Escribe tu pregunta sobre Torrelavega</label>
                  <input
                    id="torre-input"
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value.slice(0, maxLength))}
                    placeholder={outOfQuestions ? 'Límite diario alcanzado' : 'Escribe tu pregunta...'}
                    maxLength={maxLength}
                    className="w-full text-base px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 placeholder:text-slate-500 pr-12"
                    disabled={loading || outOfQuestions}
                    data-testid="assistant-input"
                  />
                  <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-bold ${charsLeft < 20 ? 'text-red-500' : 'text-slate-500'}`} aria-hidden="true">
                    {charsLeft}
                  </span>
                </div>
                {/* One large button beside the field, as messaging apps do it: a microphone while the
                    field is empty, Send once there is text. It was a 32px icon inside the field. */}
                {micAvailable && (dictating || !input.trim()) ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (dictating) { autoSend.current = false; stopDictation(); setDictating(false); return; }
                      beginDictation();
                    }}
                    aria-pressed={dictating}
                    aria-label={dictating ? 'Detener el dictado' : 'Hablar: dictar la pregunta'}
                    className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-white transition-colors disabled:opacity-30 ${
                      dictating ? 'bg-red-500 animate-pulse' : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                    disabled={loading || outOfQuestions}
                    data-testid="assistant-mic"
                  >
                    {dictating ? <MicOff size={20} aria-hidden="true" /> : <Mic size={20} aria-hidden="true" />}
                  </button>
                ) : (
                  <button
                    type="submit"
                    aria-label="Enviar pregunta"
                    disabled={loading || outOfQuestions || !input.trim()}
                    className="w-11 h-11 rounded-full flex items-center justify-center bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-30 shrink-0"
                    data-testid="assistant-send"
                  >
                    <Send size={18} aria-hidden="true" />
                  </button>
                )}
              </div>
            </form>

            {/* Footer */}
            <div className="px-3 pb-2 text-center">
              <p className="text-[11px] text-slate-500">Torre es un asistente de inteligencia artificial: responde solo con datos de SomosTorre y puede equivocarse. Comprueba lo importante.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
